import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box, Typography, Button, IconButton,
  Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchActionPlan, updateActionPlan } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function ActionPlanActionListPage() {
  const { businessDomainId, businessUnitId, actionPlanId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/action-plans/${actionPlanId}`;

  const [planData, setPlanData] = useState(null);
  const [actionPlanName, setActionPlanName] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetRow, setTargetRow] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragStartIndex = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const plan = await fetchActionPlan(actionPlanId);
      setPlanData(plan);
      setActionPlanName(plan.actionPlanName || "");
      const associated = plan.associatedActions || [];
      const sorted = [...associated].sort((a, b) => (a.actionOrder || 0) - (b.actionOrder || 0));
      setRows(
        sorted.map((a) => ({
          id: a.actionId,
          actionName: a.actionName || "",
          departmentName: a.departmentName || "",
          raw: a,
        }))
      );
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionPlanId]);

  useEffect(() => { loadData(); }, [loadData]);

  // updateActionPlan を使って associatedActions を一括更新する
  const saveActions = useCallback(async (orderedRows) => {
    if (!planData) return;
    const result = await updateActionPlan({
      ...planData,
      associatedActions: orderedRows.map((r) => r.raw),
    });
    if (result?.result === "NG") throw new Error(result.errorMessage || t("message.error"));
  }, [planData, t]);

  // ── 削除 ──────────────────────────────────────────────
  const handleDeleteClick = (row) => {
    setTargetRow(row);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!targetRow) return;
    try {
      const newRows = rows.filter((r) => r.id !== targetRow.id);
      await saveActions(newRows);
      notify.success(t("message.delete.success"));
      await loadData();
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
    setTargetRow(null);
  };

  // ── ドラッグ＆ドロップ ────────────────────────────────
  const handleDragStart = (e, index) => {
    dragStartIndex.current = index;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    const startIndex = dragStartIndex.current;
    dragStartIndex.current = null;
    if (startIndex === null || startIndex === dropIndex) return;
    const newRows = [...rows];
    const [dragged] = newRows.splice(startIndex, 1);
    newRows.splice(dropIndex, 0, dragged);
    setRows(newRows);
    try {
      await saveActions(newRows);
      await loadData();
    } catch (e) { notify.apiError(e); }
  };

  const handleDragEnd = () => {
    setDragOverIndex(null);
    dragStartIndex.current = null;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">
          {actionPlanName ? `${actionPlanName}：${t("actionPlan.actions")}` : t("actionPlan.actions")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`${basePath}/actions/add`)}
        >
          {t("common.add")}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ width: 64 }}>{t("common.order")}</TableCell>
              <TableCell>{t("actionPlan.action.name")}</TableCell>
              <TableCell>{t("actionPlan.action.department")}</TableCell>
              <TableCell sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={row.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                sx={{
                  backgroundColor: dragOverIndex === index ? "action.selected" : "inherit",
                  "&:hover": { backgroundColor: dragOverIndex === index ? "action.selected" : "action.hover" },
                  userSelect: "none",
                }}
              >
                <TableCell sx={{ cursor: "grab", color: "text.secondary", py: 0.5 }}>
                  <DragIndicatorIcon fontSize="small" />
                </TableCell>
                <TableCell sx={{ width: 80 }}>
                  <TextField
                    type="number"
                    size="small"
                    inputProps={{ min: 1, style: { width: 56 } }}
                    value={row.raw.actionOrder > 0 ? row.raw.actionOrder : index + 1}
                    onChange={(e) => {
                      const newOrder = parseInt(e.target.value, 10);
                      if (!isNaN(newOrder) && newOrder > 0) {
                        // 変更した行を更新
                        const updatedRows = rows.map((r) =>
                          r.id === row.id
                            ? { ...r, raw: { ...r.raw, actionOrder: newOrder } }
                            : r
                        );
                        // ユニーク値を昇順に並べて連番マッピングを作成（ギャップを詰める）
                        const uniqueOrders = [...new Set(updatedRows.map(r => r.raw.actionOrder || 0))]
                          .sort((a, b) => a - b);
                        const mapping = {};
                        uniqueOrders.forEach((val, idx) => { mapping[val] = idx + 1; });
                        // マッピングを全行に適用
                        const renumberedRows = updatedRows.map(r => ({
                          ...r,
                          raw: { ...r.raw, actionOrder: mapping[r.raw.actionOrder || 0] }
                        }));
                        setRows(renumberedRows);
                      }
                    }}
                    onBlur={async () => {
                      try {
                        await saveActions(rows);
                        await loadData();
                      } catch (e) { notify.apiError(e); }
                    }}
                  />
                </TableCell>
                <TableCell>{row.actionName}</TableCell>
                <TableCell>{row.departmentName}</TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleDeleteClick(row)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  {t("common.noData")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setConfirmOpen(false); setTargetRow(null); }}
      />
    </Box>
  );
}
